import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpRequest} from '@angular/common/http';
import {LocalStorageService} from '@services/local-storage.service';

/**
 * Card #1479: sent on every API call. Only then does the backend answer a call that names a client by a login
 * that was changed away from with 409 LOGIN_CHANGED (and the new login) instead of a plain 403.
 */
export const LOGIN_CHANGE_AWARE_HEADER = 'X-LS-Login-Change-Aware';
export const LOGIN_CHANGE_AWARE_VALUE = '1';

/** Asked before saving a changed email on Edit Firm and Edit Administrator, where the email is the login. */
export const CHANGED_LOGIN_EMAIL_TEXT =
  'Changing the email changes their login. They\'ll be signed out and get an email to set a password.';

const LOGIN_CHANGED = 'LOGIN_CHANGED';
const RECOVERY_ITEM = 'app.loginChangeRecovery';
// a second LOGIN_CHANGED for the same login this soon after reloading means the reload did not fix it
const RECOVERY_WINDOW_MS = 30000;

export function sameLogin(a: string, b: string): boolean {
  return (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
}

/**
 * The client's current login when the backend refused a call because the client named in it has a new one.
 */
export function changedClientLogin(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return null;
  }
  const errors = error.error?.errors;
  return Array.isArray(errors) && errors[0] === LOGIN_CHANGED && errors[1] ? errors[1] : null;
}

/**
 * Card #1479: keeps the client logins the app stores in step after a client's login changes.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientLoginChangeService {
  private reloading = false;

  constructor(
    private localStorageService: LocalStorageService,
  ) {
  }

  /**
   * Puts the new login on the stored selected client when it is the client whose login changed.
   * @returns true when the selected client was that client
   */
  applyChangedLogin(oldLogin: string, newLogin: string): boolean {
    const selected = this.localStorageService.getSelectedClient();
    if (!selected?.username || !sameLogin(selected.username, oldLogin)) {
      return false;
    }
    this.localStorageService.setSelectedClient({...selected, username: newLogin});
    return true;
  }

  /**
   * A call was refused with LOGIN_CHANGED: store the new login on the selected client when the call named it,
   * then reload the current view so everything is loaded again with the current login.
   * @returns false when this already happened for the same login moments ago, so the caller sees the error
   * instead of the page reloading forever
   */
  recover(req: HttpRequest<unknown>, newLogin: string): boolean {
    if (this.reloading) {
      return true;
    }
    if (this.recoveredRecently(newLogin)) {
      return false;
    }
    const selected = this.localStorageService.getSelectedClient();
    if (selected?.username && !sameLogin(selected.username, newLogin) && this.names(req, selected.username)) {
      this.localStorageService.setSelectedClient({...selected, username: newLogin});
    }
    try {
      sessionStorage.setItem(RECOVERY_ITEM, JSON.stringify({login: newLogin, at: Date.now()}));
    } catch {
      // without the marker we may reload once more than needed, which is harmless
    }
    this.reloading = true;
    window.location.reload();
    return true;
  }

  private recoveredRecently(login: string): boolean {
    try {
      const last = JSON.parse(sessionStorage.getItem(RECOVERY_ITEM));
      return !!last && sameLogin(last.login, login) && Date.now() - last.at < RECOVERY_WINDOW_MS;
    } catch {
      return false;
    }
  }

  /**
   * Whether the request carries this login as a whole value, in its url or its body.
   */
  private names(req: HttpRequest<unknown>, login: string): boolean {
    const texts = [this.decode(req.urlWithParams)];
    if (req.body instanceof FormData) {
      req.body.forEach((value) => typeof value === 'string' && texts.push(value));
    } else if (req.body != null) {
      texts.push(typeof req.body === 'string' ? req.body : JSON.stringify(req.body));
    }
    const escaped = login.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(^|[^A-Za-z0-9._%+@-])${escaped}($|[^A-Za-z0-9._%+@-])`, 'i');
    return texts.some((text) => pattern.test(text));
  }

  private decode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
